import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../contexts/ThemeContext";
import { useDirection } from "../../../contexts/DirectionContext";
import { Icon } from "@iconify/react";
import { Pill } from "../../common/Pill";
import { SearchType } from "../../../types/search";
import { SEARCH_OPTIONS } from "../../../constants/search";
import { motion, AnimatePresence } from "framer-motion";

interface SearchBarProps {
  value?: string;
  onSearch: (params: { query: string; type: SearchType }) => void;
  onQueryChange?: (query: string) => void;
  contentType: SearchType;
  onContentTypeChange: (type: SearchType) => void;
  placeholder?: string;
  className?: string;
  showTypeSelector?: boolean;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "transparent" | "solid";
}

const SearchBar: React.FC<SearchBarProps> = ({
  value = "",
  onSearch,
  onQueryChange,
  contentType,
  onContentTypeChange,
  placeholder,
  className = "",
  showTypeSelector = true,
  size = "md",
  variant = "default",
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  const handleQueryChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = e.target.value;
      setQuery(newQuery);
      onQueryChange?.(newQuery);
    },
    [onQueryChange]
  );

  const handleTypeChange = useCallback(
    (type: SearchType) => {
      onContentTypeChange(type);
      
      // Scroll to start after a short delay to allow for state updates
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({
            left: isRTL ? scrollContainerRef.current.scrollWidth : 0,
            behavior: 'smooth'
          });
        }
      }, 50);
    },
    [onContentTypeChange, isRTL]
  );

  const handleSearch = useCallback(() => {
    onSearch({ query: query.trim(), type: contentType });
  }, [query, contentType, onSearch]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const sizeClasses = {
    sm: "h-10",
    md: "h-12 sm:h-14",
    lg: "h-14 sm:h-16",
  };

  const variantClasses = {
    default:
      theme.mode === "dark"
        ? "bg-surface-primary bg-opacity-60 border-border-primary border-opacity-10"
        : "bg-white border-border-primary border-opacity-5",
    transparent:
      theme.mode === "dark"
        ? "bg-surface-primary/5 border-border-primary/10"
        : "bg-white/10 border-white/20",
    solid:
      theme.mode === "dark"
        ? "bg-surface-primary border-border-primary border-opacity-10"
        : "bg-white border-border-primary border-opacity-5",
  };

  const sortedOptions = useMemo(() => {
    const selected = SEARCH_OPTIONS.find(opt => opt.id === contentType);
    const others = SEARCH_OPTIONS.filter(opt => opt.id !== contentType);
    return selected ? [selected, ...others] : SEARCH_OPTIONS;
  }, [contentType]);

  // Initial scroll to selected position
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        left: isRTL ? scrollContainerRef.current.scrollWidth : 0,
        behavior: 'smooth'
      });
    }
  }, [isRTL]);

  return (
    <div className="flex flex-col gap-1 md:gap-2">
      <div className={`${className} relative z-[1000]`}>
        <div
          className={`
            relative
            flex items-stretch
            ${sizeClasses[size]}
            ${variantClasses[variant]}
            border
            rounded-full
            transition-all duration-200
            backdrop-blur-sm
            ${theme.mode === 'dark' 
              ? 'shadow-[inset_0_1px_8px_rgba(var(--colors-primary-500-rgb),0.15)]' 
              : 'shadow-[inset_0_1px_8px_rgba(var(--colors-primary-500-rgb),0.08)]'
            }
            ${isFocused
              ? "ring-1 ring-primary-500 ring-opacity-20"
              : "hover:border-primary-500 hover:border-opacity-10"
            }
          `}
        >
          <input
            type="text"
            value={query}
            onChange={handleQueryChange}
            onKeyPress={handleKeyPress}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder || t("common.searchPlaceholder")}
            className={`
              flex-1
              px-6
              bg-transparent
              text-text-primary
              placeholder-text-secondary
              text-sm sm:text-base
              focus:outline-none
              min-w-0
              transition-colors
            `}
            dir={isRTL ? "rtl" : "ltr"}
          />

          <div className="p-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSearch}
              aria-label={t("common.search")}
              className={`
                flex-shrink-0
                flex items-center justify-center
                w-10 h-10 sm:w-12 sm:h-12
                bg-primary-500
                hover:bg-primary-600
                active:bg-primary-700
                focus:outline-none
                text-white
                transition-all duration-200
                rounded-full
                shadow-lg
                group
              `}
            >
              <Icon
                icon="ph:arrow-right-thin"
                className={`
                  w-5 h-5
                  transition-transform
                  group-hover:translate-x-0.5
                  ${isRTL ? "rotate-180" : ""}
                `}
              />
            </motion.button>
          </div>
        </div>
      </div>

      {showTypeSelector && (
        <div className="w-screen -mx-4 sm:mx-0 sm:w-full">
          <div 
            className={`
              relative
              overflow-hidden 
              ${theme.mode === 'dark' ? 'bg-surface-primary/20' : 'bg-surface-primary/5'}
              py-2
              sm:rounded-2xl
              sm:px-2
            `}
          >
            <div 
              ref={scrollContainerRef}
              dir={isRTL ? 'rtl' : 'ltr'}
              className="flex gap-2 overflow-x-auto scrollbar-none px-4 sm:px-0"
              style={{
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {sortedOptions.map((option) => (
                <Pill
                  key={option.id}
                  icon={option.icon}
                  label={t(option.label)}
                  isSelected={option.id === contentType}
                  onClick={() => handleTypeChange(option.id)}
                  className="flex-shrink-0"
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
